import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { BUSINESS } from "@/lib/business-info";

// Map BUSINESS.delivery entries to display data
const PARTNERS = [
    { id: "uber-eats", name: "Uber Eats", tagline: "Fast doorstep delivery" },
    { id: "doordash", name: "DoorDash", tagline: "Citywide coverage" },
    { id: "grubhub", name: "Grubhub", tagline: "Office & home orders" },
];

const findLink = (name) =>
    BUSINESS.delivery.find((d) => d.name === name)?.url || "#";

export default function OrderDelivery() {
    return (
        <section
            id="order-delivery"
            data-testid="order-delivery-section"
            aria-label="Order delivery tonight"
            className="relative empire-section bg-charcoal"
        >
            <div className="empire-container">
                {/* Header */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-end mb-12 md:mb-16">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3">
                            <span className="h-px w-8 bg-gold" />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                Stay Home Tonight
                            </span>
                        </div>
                        <h2
                            data-testid="delivery-heading"
                            className="mt-5 empire-subhead text-cream"
                        >
                            Order delivery<br />
                            <span className="italic text-gold">tonight.</span>
                        </h2>
                    </div>
                    <p className="md:col-span-5 empire-body md:text-right max-w-md md:ml-auto">
                        Hot, fresh Caribbean food delivered to your door. Pick a
                        partner and we&rsquo;ll handle the rest.
                    </p>
                </div>

                {/* Partner tiles */}
                <ul className="grid grid-cols-1 md:grid-cols-3 gap-px bg-cream/[0.07]">
                    {PARTNERS.map((p, i) => (
                        <motion.li
                            key={p.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{
                                duration: 0.8,
                                delay: i * 0.08,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                        >
                            <a
                                href={findLink(p.name)}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`delivery-partner-${p.id}`}
                                aria-disabled="true"
                                className="group flex items-center justify-between p-8 md:p-10 bg-charcoal-800 hover:bg-charcoal-700 transition-colors duration-500"
                            >
                                <div className="min-w-0">
                                    <div className="font-serif text-cream text-[1.8rem] md:text-[2.2rem] tracking-tight leading-tight group-hover:text-gold transition-colors">
                                        {p.name}
                                    </div>
                                    <div className="mt-2 text-cream/55 text-[12px] uppercase tracking-[0.22em] font-light">
                                        {p.tagline}
                                    </div>
                                    <div
                                        className="mt-4 inline-flex items-center gap-2 text-gold text-[10px] uppercase tracking-[0.26em] font-medium"
                                        data-testid={`delivery-coming-soon-${p.id}`}
                                    >
                                        <span className="inline-block h-1 w-1 rounded-full bg-gold/80" />
                                        Coming Soon
                                    </div>
                                </div>
                                <span
                                    aria-hidden
                                    className="inline-flex h-11 w-11 items-center justify-center border border-cream/20 text-cream/70 transition-colors group-hover:border-gold group-hover:text-gold shrink-0 ml-6"
                                >
                                    <ArrowUpRight size={16} />
                                </span>
                            </a>
                        </motion.li>
                    ))}
                </ul>

                <p className="mt-8 text-cream/45 text-[12px] font-light tracking-wide">
                    Hours and menu availability vary by platform. For catering or
                    same-day large orders, call us directly.
                </p>
            </div>
        </section>
    );
}
